import { NtIdEmployee } from '../types/ntIdManagement.types';

type Props = {
  employees: NtIdEmployee[];
  onEdit: (employee: NtIdEmployee) => void;
  onDelete: (employee: NtIdEmployee) => void;

  // Server-side pagination
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalEmployees: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  // UI states
  loading?: boolean;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const NtIdTable = ({
  employees,
  onEdit,
  onDelete,
  currentPage,
  pageSize,
  totalPages,
  totalEmployees,
  onPageChange,
  onPageSizeChange,
  loading = false,
}: Props) => {
  const safeTotalPages = Math.max(1, totalPages);
  const startRecord =
    totalEmployees === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalEmployees);

  const getPageNumbers = (): (number | 'ellipsis-start' | 'ellipsis-end')[] => {
    if (safeTotalPages <= 5) {
      return Array.from({ length: safeTotalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 'ellipsis-end', safeTotalPages];
    }

    if (currentPage >= safeTotalPages - 2) {
      return [
        1,
        'ellipsis-start',
        safeTotalPages - 3,
        safeTotalPages - 2,
        safeTotalPages - 1,
        safeTotalPages,
      ];
    }

    return [
      1,
      'ellipsis-start',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      'ellipsis-end',
      safeTotalPages,
    ];
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="overflow-hidden rounded-lg border border-[#ECE8DE] bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-[1300px] w-full border-collapse">
          <thead className="bg-[#F8F9FB]">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
              <th className="px-3 py-3">Employee ID</th>
              <th className="px-3 py-3">Employee Name</th>
              <th className="px-3 py-3">Email</th>
              <th className="px-3 py-3">NT IDs</th>
              <th className="px-3 py-3">Role</th>
              <th className="px-3 py-3">Team</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Reporting Manager</th>
              <th className="px-3 py-3">Shift</th>
              <th className="px-3 py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-[#6B7280]">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#D7DCE2] border-t-[#2563EB]" />
                    Loading employees...
                  </div>
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center">
                  <p className="text-base font-semibold text-[#231F16]">
                    No Employees Found
                  </p>
                  <p className="mt-1 text-xs text-[#8A8577]">
                    Try changing your search or add a new employee.
                  </p>
                </td>
              </tr>
            ) : (
              employees.map((employee) => (
                <tr
                  key={`${employee.employeeNetworkMappingId}-${employee.employeeCode}-${employee.ntIds[0] ?? ''}`}
                  className="border-t border-[#F1F3F5] text-[12px] hover:bg-[#FAFBFC]"
                >
                  <td className="whitespace-nowrap px-3 py-2 font-medium">
                    {employee.employeeCode || '—'}
                  </td>

                  <td className="whitespace-nowrap px-3 py-2">
                    {employee.employeeName || '—'}
                  </td>

                  <td className="whitespace-nowrap px-3 py-2 text-[#6B7280]">
                    {employee.email || '—'}
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {employee.ntIds.length > 0 ? (
                        employee.ntIds.map((ntId, index) => (
                          <span
                            key={`${ntId}-${index}`}
                            className="rounded-full bg-[#F3EEFF] px-2 py-0.5 text-[10px] font-medium text-[#5B3CC4]"
                          >
                            {ntId}
                          </span>
                        ))
                      ) : (
                        <span className="text-[#8A8577]">—</span>
                      )}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-3 py-2">
                    {employee.role || '—'}
                  </td>

                  <td className="whitespace-nowrap px-3 py-2">
                    {employee.team || '—'}
                  </td>

                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        employee.status === 'Active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-600'
                      }`}
                    >
                      {employee.status || '—'}
                    </span>
                  </td>

                  <td className="whitespace-nowrap px-3 py-2">
                    {employee.reportingManager || '—'}
                  </td>

                  <td className="whitespace-nowrap px-3 py-2">
                    {employee.shift || '—'}
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(employee)}
                        className="flex h-7 w-7 items-center justify-center rounded-md border border-[#D7DCE2] text-[#2563EB] transition hover:bg-blue-50"
                        title="Edit employee"
                        aria-label={`Edit ${employee.employeeName || employee.employeeCode}`}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </button>

                      {/* <button
                        type="button"
                        onClick={() => onDelete(employee)}
                        className="flex h-7 w-7 items-center justify-center rounded-md border border-red-200 text-red-600 transition hover:bg-red-50"
                        title="Delete employee"
                        aria-label={`Delete ${employee.employeeName || employee.employeeCode}`}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex flex-col gap-3 border-t border-[#ECE8DE] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#6B7280]">
          <span>
            Showing <strong className="text-[#231F16]">{startRecord}</strong>
            {' '}to <strong className="text-[#231F16]">{endRecord}</strong>
            {' '}of <strong className="text-[#231F16]">{totalEmployees}</strong>
            {' '}employees
          </span>

          <label htmlFor="ntid-page-size" className="ml-1">
            Rows per page:
          </label>

          <select
            id="ntid-page-size"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            disabled={loading}
            className="rounded-md border border-[#D7DCE2] bg-white px-2 py-1 text-xs text-[#231F16] outline-none focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={loading || currentPage <= 1}
            className="rounded-md border border-[#D7DCE2] px-3 py-1.5 text-xs text-[#374151] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            Previous
          </button>

          {pageNumbers.map((page, index) =>
            typeof page === 'number' ? (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                disabled={loading || page === currentPage}
                aria-current={page === currentPage ? 'page' : undefined}
                className={`min-w-8 rounded-md border px-2.5 py-1.5 text-xs transition ${
                  page === currentPage
                    ? 'border-[#2563EB] bg-[#2563EB] font-semibold text-white'
                    : 'border-[#D7DCE2] text-[#374151] hover:bg-gray-50'
                } disabled:cursor-not-allowed`}
              >
                {page}
              </button>
            ) : (
              <span
                key={`${page}-${index}`}
                className="px-1 text-xs text-[#8A8577]"
                aria-hidden="true"
              >
                …
              </span>
            ),
          )}

          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={loading || currentPage >= safeTotalPages}
            className="rounded-md border border-[#D7DCE2] px-3 py-1.5 text-xs text-[#374151] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default NtIdTable;
